import React, { memo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { ExtractionConfidence } from "@investorRequest/models/bankExtraction";
import { bankExtractionCopy } from "@investorRequest/constants/bankExtractionCopy";

/** Screen-reader-only text — must use px strings; MUI spacing units (e.g. `1`) are 8px and leak content. */
export const visuallyHiddenSx = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap" as const,
  border: 0,
};

interface Props {
  confidence?: ExtractionConfidence;
  describedById?: string;
}

const HighGlyph = () => (
  <Box component="span" aria-hidden sx={{ color: "warning.main", fontSize: 16, lineHeight: 1 }}>
    ●
  </Box>
);

const LowGlyph = () => (
  <Box component="span" aria-hidden sx={{ color: "warning.main", fontSize: 14, lineHeight: 1 }}>
    ▲
  </Box>
);

export const BankExtractionFieldChrome = ({ confidence, describedById }: Props) => {
  if (!confidence || confidence === "missing") return null;

  const glyph = confidence === "high" ? <HighGlyph /> : <LowGlyph />;
  const tooltip = confidence === "high" ? undefined : bankExtractionCopy.lowConfidenceTooltip;

  const content = (
    <Box data-testid={`bank-extraction-chrome-${confidence}`} aria-describedby={describedById}>
      {glyph}
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow describeChild>
        {content}
      </Tooltip>
    );
  }

  return content;
};

interface LegendProps {
  compact?: boolean;
}

export const BankExtractionConfidenceLegend = ({ compact }: LegendProps) => (
  <Box
    data-testid="bank-extraction-confidence-legend"
    display="flex"
    alignItems="center"
    gap={compact ? 2 : 3}
    aria-hidden={false}
  >
    <Box display="flex" alignItems="center" gap={0.5}>
      <HighGlyph />
      <Typography variant="caption">{bankExtractionCopy.confidenceLegendHigh}</Typography>
    </Box>
    <Box display="flex" alignItems="center" gap={0.5}>
      <LowGlyph />
      <Typography variant="caption">{bankExtractionCopy.confidenceLegendReview}</Typography>
    </Box>
  </Box>
);

export const BankExtractionFieldDescription = ({
  id,
  confidence,
}: {
  id: string;
  confidence: ExtractionConfidence;
}) => {
  const ariaDescriptions: Record<ExtractionConfidence, string> = {
    high: bankExtractionCopy.ariaHighConfidence,
    low: bankExtractionCopy.ariaLowConfidence,
    missing: bankExtractionCopy.ariaMissing,
  };
  const text = ariaDescriptions[confidence];

  return (
    <Typography id={id} component="span" sx={visuallyHiddenSx}>
      {text}
    </Typography>
  );
};

export default memo(BankExtractionFieldChrome);
